import { makeObservable, observable, action, runInAction } from "mobx";
import ProjectPackagingOpsAPI from "../../api/ProjectPackagingOpsAPI";
import {
  InventoryProduct,
  PackagingSwagpackDetails,
  PackCreationData,
  PackagingSwagpackMedia,
  SourcingSwagpackItem,
  PackagingSwagpack,
  SwagpackProduct,
  Variant,
  ProductData,
  VariantToDelete,
} from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import { APIResponse } from "../../../../models/NetworkResponse";

export class ProjectPackagingOpsStore {
  ProjectPackagingOpsAPI: ProjectPackagingOpsAPI;
  originalData: PackagingSwagpackDetails;
  packagingRequestBody: PackagingSwagpackDetails;
  packagingOpsSwagpackMedia: PackagingSwagpackMedia;
  packagingSourcingSwagpacks: SourcingSwagpackItem[];
  createdSwagPackDetails: PackCreationData[] = [];
  skus: { [key: string]: string } = {};
  isPackNameManuallyEdited: boolean = false;
  removePackPopupOpen: boolean = false;
  packToDelete: {
    inventory_id: number;
    swagpack_id: number;
  } | null = null;
  removequantity: number | string = 0;
  variantSelections: VariantToDelete[] = [];
  variantAvailableQuantities: { [key: number]: number } = {};
  selectedVariantIds: number[] = [];

  constructor() {
    this.ProjectPackagingOpsAPI = new ProjectPackagingOpsAPI();
    this.originalData = {} as PackagingSwagpackDetails;
    this.packagingRequestBody = {} as PackagingSwagpackDetails;
    this.packagingOpsSwagpackMedia = { media: [], archived: 0 };
    this.packagingSourcingSwagpacks = [];
    this.removePackPopupOpen = false;
    this.packToDelete = null;
    this.removequantity = 0;
    this.variantSelections = [];
    this.variantAvailableQuantities = {};
    this.selectedVariantIds = [];
    makeObservable(this, {
      originalData: observable,
      packagingRequestBody: observable,
      packagingOpsSwagpackMedia: observable,
      packagingSourcingSwagpacks: observable,
      skus: observable,
      isPackNameManuallyEdited: observable,
      removequantity: observable,
      getSourcingSwagpacks: action,
      getPackagingOpsSwagpackImageVideo: action,
      getProjectPackagingOpsDetails: action,
      deletePackagingOpsSwagpack: action,
      uploadPackagingImageVideo: action,
      createSwagPack: action,
      createInventory: action,
      updateSwagpackLabel: action,
      updateSwagpackQuantity: action,
      deletePackagingMedia: action,
      setSku: action,
      setPackNameManuallyEdited: action,
      uploadPackagingPackImage: action,
      removePackPopupOpen: observable,
      setRemovePackPopupOpen: action,
      packToDelete: observable,
      setPackToDelete: action,
      setRemoveQuantity: action,
      variantSelections: observable,
      variantAvailableQuantities: observable,
      selectedVariantIds: observable,
      setVariantSelections: action,
      updateVariantSelection: action,
      toggleVariantSelection: action,
      setVariantAvailableQuantities: action,
      clearSelectedVariantIds: action,
    });
  }

  getProjectPackagingOpsDetails = async (projectId: number) => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.getPackagingOpsSwagpacks(projectId);
      this.originalData = response;
      this.packagingRequestBody = response;
    } catch (error: any) {
      throw error;
    }
  };

  getSourcingSwagpacks = async (projectId: number) => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.getSourcingSwagpacks(projectId);
      runInAction(() => {
        this.packagingSourcingSwagpacks = response;
      });
    } catch (error) {
      throw error;
    }
  };

  getPackagingOpsSwagpackImageVideo = async (
    projectId: number,
    inventoryId: number,
  ) => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.getPackagingOpsSwagpackImageVideo(
          projectId,
          inventoryId,
        );
      this.packagingOpsSwagpackMedia = response.result;
    } catch (error) {
      throw error;
    }
  };

  uploadPackagingImageVideo = async (
    projectId: number,
    inventoryId: number,
    urls: string[],
    isImageUrl: boolean,
  ): Promise<void> => {
    try {
      await this.ProjectPackagingOpsAPI.uploadPackagingImageVideo(
        projectId,
        inventoryId,
        urls,
        isImageUrl,
      );
    } catch (error) {
      throw error;
    }
  };

  calculateUsedQuantity = () => {
    const usedQuantityMap: Record<number, number> = {};
    const inwardQuantityMap: Record<number, number> = {};

    const swagpacks = this.packagingRequestBody.swagpacks;
    const inventoryType = this.packagingRequestBody?.inventory_type;

    swagpacks?.forEach((pack: PackagingSwagpack) => {
      if (pack.products) {
        pack.products.forEach((product: SwagpackProduct) => {
          if (product.variants) {
            product.variants.forEach(
              (variant: Variant, variantIndex: number) => {
                const productId = variant.product_id;
                const initialQty = variant.initial_quantity || 0;
                const quantity_this_order = Number(
                  pack.quantity_this_order || 0,
                );

                if (!usedQuantityMap[productId]) {
                  usedQuantityMap[productId] = 0;
                }

                const perPackVariantQuantity = initialQty || 1;

                if (pack.products.length === 1) {
                  if (inventoryType === 1) {
                    if (variantIndex === 0) {
                      usedQuantityMap[productId] += quantity_this_order
                        ? quantity_this_order
                        : initialQty;
                    }
                  } else {
                    usedQuantityMap[productId] += quantity_this_order
                      ? quantity_this_order
                      : initialQty;
                  }
                } else {
                  usedQuantityMap[productId] += quantity_this_order
                    ? quantity_this_order * perPackVariantQuantity
                    : initialQty * (pack.quantity || 1);
                }
              },
            );
          }
        });
      }
    });

    return { usedQuantityMap, inwardQuantityMap };
  };

  filterProductsForPopup = (): ProductData[] => {
    const sourcingSwagpacks = this.packagingSourcingSwagpacks || [];
    const packagingProducts = this.packagingRequestBody.products || [];
    const { usedQuantityMap } = this.calculateUsedQuantity();

    const sourcingProductIds = sourcingSwagpacks.map(
      (item: SourcingSwagpackItem) => item.product_id,
    );

    return packagingProducts
      .filter(
        (product) =>
          sourcingProductIds.includes(product.productId) &&
          product.options &&
          Object.keys(product.options).length > 0,
      )
      .map((product) => {
        const sourcingSwagpack = sourcingSwagpacks.find(
          (item: SourcingSwagpackItem) => item.product_id === product.productId,
        );

        const backfillAllowed = sourcingSwagpack?.backfill_allowed || false;

        const orderQuantity = sourcingSwagpack?.order_quantity || 0;
        const totalReceived = sourcingSwagpack?.total_received_quantity || 0;
        const totalRejected = sourcingSwagpack?.total_rejected_quantity || 0;
        const usedQuantity = usedQuantityMap[product.productId] || 0;

        const availableQuantity =
          backfillAllowed === false
            ? totalReceived - totalRejected - usedQuantity
            : orderQuantity - usedQuantity;

        return {
          ...product,
          availableQuantity,
        };
      });
  };

  createSwagPack = async (
    data: PackCreationData,
    projectId?: number,
  ): Promise<void> => {
    try {
      const response = await this.ProjectPackagingOpsAPI.createSwagPack(data);

      if (response) {
        runInAction(() => {
          this.createdSwagPackDetails = response;
        });

        // Refresh the data after successful creation if projectId is provided
        if (projectId) {
          await this.refreshProjectData(projectId);
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  refreshProjectData = async (projectId: number): Promise<void> => {
    try {
      await Promise.all([
        this.getProjectPackagingOpsDetails(projectId),
        this.getSourcingSwagpacks(projectId),
      ]);

      const inventoryId = this.packagingRequestBody.inventory_id;
      if (inventoryId) {
        await this.getPackagingOpsSwagpackImageVideo(projectId, inventoryId);
      }
    } catch (error: any) {
      throw error;
    }
  };

  deletePackagingOpsSwagpack = async (
    inventory_id: number,
    swagpack_id: number,
    payload?: {
      pack_remove_quantity?: number;
      bulk_remove_quantity?: VariantToDelete[];
    },
  ): Promise<APIResponse> => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.deletePackagingOpsSwagpack(
          inventory_id,
          swagpack_id,
          payload,
        );
      if (response) {
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  completeProject = async (projectId: number) => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.completeProject(projectId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };
  createInventory = async (
    customerId: number,
    label: string,
    orderId: number,
    type: number,
  ): Promise<void> => {
    try {
      const response = await this.ProjectPackagingOpsAPI.createInventory(
        customerId,
        label,
        orderId,
        type,
      );
      if (response) {
        runInAction(() => {});
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateSwagpackLabel = async (
    inventoryId: number,
    swagpackId: number,
    label: string,
  ): Promise<void> => {
    try {
      const response = await this.ProjectPackagingOpsAPI.updateSwagpackLabel(
        inventoryId,
        swagpackId,
        label,
      );
      if (response) {
        runInAction(() => {
          this.packagingRequestBody.swagpacks =
            this.packagingRequestBody.swagpacks?.map((swagpack) =>
              Number(swagpack.swagpack_id) === Number(swagpackId)
                ? { ...swagpack, swagpack_name: label }
                : swagpack,
            );
        });
      }
    } catch (error) {
      throw error;
    }
  };
  updateSwagpackQuantity = async (
    swagpack_id: number,
    packs_count: number,
    products: InventoryProduct[],
    inventory_id: number,
    order_id: number,
  ): Promise<APIResponse> => {
    try {
      const response = await this.ProjectPackagingOpsAPI.updateSwagpackQuantity(
        swagpack_id,
        packs_count,
        inventory_id,
        products,
        order_id,
      );
      if (response) {
        runInAction(() => {
          this.packagingRequestBody.swagpacks =
            this.packagingRequestBody.swagpacks?.map((swagpack) =>
              Number(swagpack.swagpack_id) === Number(swagpack_id)
                ? {
                    ...swagpack,
                    quantity: (swagpack.quantity || 0) + packs_count,
                    products_count:
                      (swagpack.products_count || 0) + packs_count,
                  }
                : swagpack,
            );
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  deletePackagingMedia = async (
    order_id: number,
    inventory_id: number,
    media_id: number,
  ): Promise<APIResponse> => {
    try {
      const response = await this.ProjectPackagingOpsAPI.deletePackagingMedia(
        order_id,
        inventory_id,
        media_id,
      );
      if (response?.success) {
        runInAction(() => {
          this.packagingOpsSwagpackMedia.media =
            this.packagingOpsSwagpackMedia.media.filter(
              (media) => media.id !== media_id,
            );
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  setSku = (key: string, value: string) => {
    this.skus[key] = value;
  };

  generateSku = (productId: number, selectedSizeId: string, key?: string) => {
    const skuKey = key || productId.toString();
    const product = this.packagingRequestBody.products?.find(
      (p) => p.productId === productId,
    );
    const sizeName =
      product?.options?.Size?.find((s) => s.id.toString() === selectedSizeId)
        ?.name || ""; // Old SKU generation logic (kept for reference):
    // const customerId = this.packagingRequestBody.customerId || 0;
    // const inventoryId = this.packagingRequestBody.inventory_id || 0;
    // const size = product?.options?.Size?.find(
    //   (s) => s.id.toString() === selectedSizeId,
    // );
    // const sizeName = size?.name || "";
    // const oneSizeVariations = [
    //   "One Size",
    //   "onesize",
    //   "Onesize",
    //   "One-size",
    //   "one size",
    //   "ONE SIZE",
    // ];
    // const normalizedSizeName = sizeName.trim().toLowerCase();
    // const isOneSize = oneSizeVariations.some(
    //   (variation) => variation.toLowerCase() === normalizedSizeName,
    // );
    // const generatedSku = isOneSize
    //   ? `SE${customerId}-${inventoryId}-${productId}`
    //   : `SE${customerId}-${inventoryId}-${productId}-${sizeName}`;
    // this.skus[skuKey] = generatedSku;
    // return generatedSku;
    // Use selected size name as client SKU (no generation).

    this.skus[skuKey] = sizeName;
    return sizeName;
  };

  clearSkuForProduct = (productId: number, key?: string) => {
    const skuKey = key || productId.toString();
    if (this.skus[skuKey]) {
      delete this.skus[skuKey];
    }
  };

  clearAllSkus = () => {
    this.skus = {};
  };

  setPackNameManuallyEdited = (value: boolean) => {
    this.isPackNameManuallyEdited = value;
  };

  uploadPackagingPackImage = async (
    inventoryId: number,
    swagpack_id: number,
    imageUrl: string,
  ): Promise<APIResponse> => {
    try {
      const response =
        await this.ProjectPackagingOpsAPI.uploadPackagingPackImage(
          swagpack_id,
          inventoryId,
          imageUrl,
        );
      if (response) {
        runInAction(() => {
          this.packagingRequestBody.swagpacks =
            this.packagingRequestBody.swagpacks?.map((swagpack) =>
              Number(swagpack.swagpack_id) === Number(swagpack_id)
                ? { ...swagpack, image_url: imageUrl }
                : swagpack,
            );
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };
  setRemovePackPopupOpen = (value: boolean) => {
    this.removePackPopupOpen = value;
  };
  setPackToDelete = (
    value: {
      inventory_id: number;
      swagpack_id: number;
    } | null,
  ) => {
    this.packToDelete = value;
  };
  setRemoveQuantity = (value: number | string) => {
    this.removequantity = value;
  };

  setVariantSelections = (selections: VariantToDelete[]) => {
    this.variantSelections = selections;
    this.selectedVariantIds = [];
  };

  setVariantAvailableQuantities = (quantities: { [key: number]: number }) => {
    this.variantAvailableQuantities = quantities;
  };

  clearSelectedVariantIds = () => {
    this.selectedVariantIds = [];
  };

  updateVariantSelection = (variantId: number, quantity: number | string) => {
    const index = this.variantSelections.findIndex((v) => v.id === variantId);
    if (index !== -1) {
      if (quantity === "" || quantity === null || quantity === undefined) {
        this.variantSelections[index].quantity = 0;
      } else {
        this.variantSelections[index].quantity = Number(quantity) || 0;
      }
    }
  };

  toggleVariantSelection = (variantId: number) => {
    const index = this.variantSelections.findIndex((v) => v.id === variantId);
    if (index !== -1) {
      const availableQty = this.variantAvailableQuantities[variantId] || 0;
      const isSelected = this.selectedVariantIds.includes(variantId);

      if (isSelected) {
        this.selectedVariantIds = this.selectedVariantIds.filter(
          (id) => id !== variantId,
        );
        this.variantSelections[index].quantity = 0;
      } else {
        this.selectedVariantIds.push(variantId);
        this.variantSelections[index].quantity = availableQty;
      }
    }
  };
}
